import { forwardRef, lazy, Suspense, useEffect, useRef, useState } from "react";
import {
  AnalyticsTrackingEvent,
  CampaignType,
  getPurchaseRewardAmountForProduct,
  isProductEligibleForCheckout,
  ProductRewardSearchResult,
  RewardPillWithText,
  RewardUnlockStrategy,
} from "@brandclub/common-ui";
import BuyBoxDialog from "../../components/dialog/BuyboxDialog";
import { useTrackActions } from "../../hooks/useTracking";
import { useBCRouting } from "../../providers/BCRoutingContextProvider";
import { styled } from "@mui/material";
import { formatMoney } from "../../utils/string";
import { HOST_DOMAIN } from "../../utils/domain";
import { appThemeQueryOptions } from "@/api/cache/appThemeQueryOptions";
import { useQuery } from "@tanstack/react-query";
import { useDomainConfig } from "@/api/cache/appConfigQueryOptions";
import { useCurrentBrandclubProduct } from "@/api/cache/dtcProductQueryOptions";
import { useAllCarts, useCartUpdates } from "@/api/cache/cartQueryOptions";
import BagDrawer from "../BagButton/BagDrawer";

const LocationMap = lazy(() => import("./LocationMap"));

const DEFAULT_ADD_TO_BAG_TEXT = "Add to bag  •  ${price}";

const AddToBagButtonContainer = styled("div")<{
  fontSize: number;
  buttonHeight: number;
  borderColor: string;
  borderWidth: number;
  bgColor?: string;
  textColor: string;
  fullWidth: boolean;
  fontWeight?: string;
  borderRadius: number;
  horizontalPadding: number;
}>(
  ({
    fontSize,
    borderColor,
    borderWidth,
    bgColor,
    textColor,
    fullWidth,
    fontWeight,
    buttonHeight,
    borderRadius,
    horizontalPadding,
    theme,
  }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "16px",

    width: fullWidth ? "100%" : "fit-content",
    ".bc-add-to-bag": {
      fontFamily: theme.typography.button.fontFamily,
      whiteSpace: "pre",
      zIndex: 50,
      fontSize: +fontSize,
      height: +buttonHeight,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: bgColor,
      color: textColor,
      paddingLeft: +horizontalPadding,
      paddingRight: +horizontalPadding,
      borderRadius: +borderRadius,
      fontWeight: fontWeight,
      cursor: "pointer",
      width: fullWidth ? "100%" : "fit-content",
      border: `${+borderWidth}px solid ${borderColor}`,
      textAlign: "center",
    },
  })
);

const ScrollSliderContainer = styled("div")(({ theme }) => ({
  width: "100vw",
  position: "fixed",
  bottom: 0,
  left: 0,
  boxShadow: "0 3px 6px 0 rgba(31, 27, 27, 0.16)",
  ".scroll-slider-inner": {
    gap: "14px",
    borderTop: "1px solid rgba(220, 221, 220, 0.5)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "17px 34px",
    width: "100%",
    backgroundColor:
      theme.componentStyleOverrides?.dialog?.backgroundColor || "#ffffff",
    color: theme.componentStyleOverrides?.dialog?.textColor || "#fff",
    fontSize: "12px",
    fontWeight: "bold",
  },
  transition: "transform 0.6s",
  zIndex: 100,
  "&.scroll-on": {
    transform: "translateY(0)",
  },
  "&.scroll-off": {
    transform: "translateY(105%)",
  },
}));

const AddToBagButton = forwardRef<
  HTMLDivElement,
  {
    mobile?: boolean;
    currentBrandclubProduct?: ProductRewardSearchResult;
    handleBuyClick: () => void;
  }
>(({ currentBrandclubProduct, handleBuyClick, mobile }, ref) => {
  const paramName = mobile ? "buyButtonMobile" : "buyButton";

  const { data: cartProducts } = useAllCarts({
    select: (data) => data.products,
    // only read from cache to avoid resetting updated local carts
    enabled: false, // Prevents fetching
    staleTime: Infinity, // Prevents background refetches
  });
  const { data: styleData } = useQuery(appThemeQueryOptions());

  if (!currentBrandclubProduct) {
    return null;
  }

  const fontSize = styleData?.inpageCheckoutComponent?.[paramName]?.fontSize;
  const borderColor =
    styleData?.inpageCheckoutComponent?.[paramName]?.borderColor;
  const borderWidth =
    styleData?.inpageCheckoutComponent?.[paramName]?.borderWidth;
  const buttonHeight =
    styleData?.inpageCheckoutComponent?.[paramName]?.buttonHeight;
  const addToBagText =
    styleData?.inpageCheckoutComponent?.[paramName]?.text ||
    DEFAULT_ADD_TO_BAG_TEXT;
  const textColor =
    styleData?.inpageCheckoutComponent?.[paramName]?.fontColor || "#ffffff";
  const fullWidth =
    !!styleData?.inpageCheckoutComponent?.[paramName]?.fullWidth;
  const fontWeight =
    styleData?.inpageCheckoutComponent?.[paramName]?.fontWeight;
  const borderRadius =
    styleData?.inpageCheckoutComponent?.[paramName]?.borderRadius || 999;
  const horizontalPadding =
    styleData?.inpageCheckoutComponent?.[paramName]?.horizontalPadding || 20;

  const bgColor =
    styleData?.inpageCheckoutComponent?.[paramName]?.backgroundColor ||
    styleData?.primary;

  const priceAmount = formatMoney(currentBrandclubProduct?.retailPrice || 0);

  const displayAddToBagText = addToBagText.replace("${price}", priceAmount);

  const cartProduct = currentBrandclubProduct?.stacklineSku
    ? cartProducts?.[currentBrandclubProduct?.stacklineSku]
    : currentBrandclubProduct;

  const metadata = cartProduct ?? currentBrandclubProduct;
  // hasActiveInstantReward on metadata is not always set, so we need to check the campaignRewards as well
  const hasActiveInstantReward =
    !!metadata?.hasActiveInstantReward ||
    (!!metadata?.campaignRewards &&
      metadata?.campaignRewards.some(
        (campaignReward) =>
          campaignReward?.campaignType ===
            CampaignType.DailyDealsRewardCampaign ||
          (campaignReward &&
            "rewardUnlockStrategy" in campaignReward &&
            campaignReward.rewardUnlockStrategy ===
              RewardUnlockStrategy.Instant)
      ));

  const rewardAmount = metadata && getPurchaseRewardAmountForProduct(metadata);
  const rewardPercent =
    !!metadata?.retailPrice && !!rewardAmount
      ? (rewardAmount * 100) / metadata.retailPrice
      : 0;

  return (
    <AddToBagButtonContainer
      ref={ref}
      fontSize={fontSize || 16}
      borderColor={borderColor || "#000"}
      borderWidth={borderWidth || 0}
      buttonHeight={buttonHeight || 50}
      fullWidth={fullWidth}
      fontWeight={fontWeight}
      borderRadius={borderRadius}
      horizontalPadding={horizontalPadding}
      bgColor={bgColor}
      textColor={textColor}
    >
      <button
        className="bc-add-to-bag"
        onClick={() => {
          handleBuyClick();
        }}
        type="button"
      >
        {displayAddToBagText}
      </button>
      {!!rewardAmount && (
        <RewardPillWithText
          rewardAmount={rewardAmount}
          baseFontSize={mobile ? 12 : 14}
          pillContainerStyle={{
            boxSizing: "border-box",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          rewardPercent={rewardPercent}
          rewardCampaignType={
            hasActiveInstantReward
              ? CampaignType.DailyDealsRewardCampaign
              : CampaignType.PurchaseRewardCampaign
          }
        />
      )}
    </AddToBagButtonContainer>
  );
});

const InpageAddToBagButton = ({ mobile }: { mobile?: boolean }) => {
  const paramName = mobile ? "buyButtonMobile" : "buyButton";
  const [isScrollSliderOn, setIsScrollSliderOn] = useState(false);
  const [isBuyBoxOpen, setIsBuyBoxOpen] = useState(false);
  const inpageButtonRef = useRef<HTMLDivElement | null>(null);
  const { mutate: updateCart } = useCartUpdates();

  useEffect(() => {
    const handleScroll = () => {
      if (inpageButtonRef.current) {
        const rect = inpageButtonRef.current.getBoundingClientRect();
        setIsScrollSliderOn(rect.top < 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const { data: styleData } = useQuery(appThemeQueryOptions());

  const enableScrollSliderDisplay =
    !!styleData?.inpageCheckoutComponent?.[paramName]
      ?.enableScrollSliderDisplay;

  const enableLocationMap =
    !!styleData?.inpageCheckoutComponent?.[paramName]?.enableLocationMap;
  const bcRouting = useBCRouting();
  const [trackCheckoutBarAction] = useTrackActions();

  const { data: domainConfig } = useDomainConfig();

  const { data: currentBrandclubProduct } = useCurrentBrandclubProduct();

  const handleBuyClick = () => {
    const product = currentBrandclubProduct;
    if (!product) {
      return;
    }

    if (product?.matchedProducts?.length) {
      setIsBuyBoxOpen(true);
      trackCheckoutBarAction(
        AnalyticsTrackingEvent.WEB_APP_ORGANIC_OPEN_VIEW_OFFERS,
        {
          offersTotal: product.matchedProducts.length,
        }
      );
      BuyBoxDialog.show(product, () => {
        setIsBuyBoxOpen(false);
      });
    } else if (
      isProductEligibleForCheckout(
        product,
        domainConfig?.checkoutAllowedOfferType
      )
    ) {
      updateCart({
        action: "add",
        items: [{ ...product, quantity: 1 }],
        retailerId: product.retailerId,
      });
      trackCheckoutBarAction(
        AnalyticsTrackingEvent.WEB_APP_ORGANIC_ADD_TO_CART,
        {
          stacklineSku: product.stacklineSku,
          retailerId: product.retailerId,
          retailerSku: product.retailerSku,
          retailPrice: product.retailPrice,
          quantity: 1,
        }
      );
      BagDrawer?.show();
    } else {
      trackCheckoutBarAction(
        AnalyticsTrackingEvent.OPEN_RETAILER_PRODUCT_PAGE,
        {
          stacklineSku: product.stacklineSku,
          retailerId: product.retailerId,
          retailerSku: product.retailerSku,
          retailPrice: product.retailPrice,
        }
      );

      bcRouting.navigate(product.retailerProductPageUrl, "_blank");
    }
  };

  return (
    <>
      <AddToBagButton
        ref={inpageButtonRef}
        mobile={mobile}
        handleBuyClick={handleBuyClick}
        currentBrandclubProduct={currentBrandclubProduct}
      />
      {enableLocationMap ? (
        <div style={{ marginTop: "24px" }}>
          <Suspense fallback={null}>
            <LocationMap
              mobile={mobile}
              stacklineSku={currentBrandclubProduct?.stacklineSku}
              API_URL={HOST_DOMAIN}
              product={currentBrandclubProduct}
              onAddProductToBag={() => {
                handleBuyClick();
              }}
            />
          </Suspense>
        </div>
      ) : null}
      {/* // if the buybox is open, we don't want to show the scroll slider */}
      {enableScrollSliderDisplay && !isBuyBoxOpen && currentBrandclubProduct ? (
        <ScrollSliderContainer
          className={isScrollSliderOn ? "scroll-on" : "scroll-off"}
        >
          <div className="scroll-slider-inner">
            <AddToBagButton
              mobile={mobile}
              handleBuyClick={handleBuyClick}
              currentBrandclubProduct={currentBrandclubProduct}
            />
          </div>
        </ScrollSliderContainer>
      ) : null}
    </>
  );
};

export default InpageAddToBagButton;
